<template>
  <section class="mobile-sidebar-list-navigation" :aria-label="label">
    <button
      type="button"
      class="mobile-sidebar-list-navigation-toggle"
      :class="{ 'is-current': active }"
      :aria-current="active ? 'page' : null"
      :aria-expanded="authenticated ? expanded : null"
      :aria-controls="authenticated ? controlId : null"
      @click="toggle"
    >
      <span class="mobile-sidebar-list-navigation-icon" aria-hidden="true">
        <el-icon><component :is="sectionIcon" /></el-icon>
      </span>
      <span class="mobile-sidebar-list-navigation-label">{{ label }}</span>
      <el-icon v-if="authenticated" class="mobile-sidebar-list-navigation-chevron" aria-hidden="true"><ArrowRight /></el-icon>
    </button>
    <div v-if="authenticated" :id="controlId" v-show="expanded" class="mobile-sidebar-list-navigation-options">
      <button
        v-if="overviewPath"
        type="button"
        class="mobile-sidebar-list-navigation-item"
        :class="{ 'is-current': overviewActive }"
        :aria-current="overviewActive ? 'page' : null"
        @click="$emit('select-overview', overviewPath)"
      >
        <span class="mobile-sidebar-list-navigation-icon" aria-hidden="true">
          <el-icon><component :is="sectionIcon" /></el-icon>
        </span>
        <span class="mobile-sidebar-list-navigation-copy">{{ overviewLabel }}</span>
      </button>
      <div v-if="!authenticated" class="mobile-sidebar-list-navigation-status">
        <button type="button" class="mobile-sidebar-list-navigation-action" @click="$emit('request-login')">
          {{ loginLabel }}
        </button>
      </div>
      <template v-else>
        <div v-if="items.length" class="mobile-sidebar-list-navigation-list">
          <button
            v-for="item in items"
            :key="item[itemKeyField]"
            type="button"
            class="mobile-sidebar-list-navigation-item"
            :class="{ 'is-current': isCurrent(item) }"
            :aria-current="isCurrent(item) ? 'page' : null"
            :aria-label="item[itemLabelField]"
            @click="$emit('select', item)"
          >
            <span class="mobile-sidebar-list-navigation-icon" aria-hidden="true"><el-icon><Folder /></el-icon></span>
            <span class="mobile-sidebar-list-navigation-copy">{{ item[itemLabelField] }}</span>
          </button>
        </div>
        <p v-if="loading" role="status" class="mobile-sidebar-list-navigation-status">{{ $t('common.loading') }}</p>
        <p v-else-if="error" role="alert" class="mobile-sidebar-list-navigation-status">
          {{ errorLabel }}
          <button type="button" class="mobile-sidebar-list-navigation-action" @click="$emit('retry')">
            {{ retryLabel }}
          </button>
        </p>
        <p v-else-if="!items.length" class="mobile-sidebar-list-navigation-status">{{ emptyLabel }}</p>
        <button
          v-if="items.length && hasMore && !loading && !error"
          type="button"
          class="mobile-sidebar-list-navigation-action mobile-sidebar-list-navigation-more"
          @click="$emit('load-more')"
        >{{ moreLabel }}</button>
      </template>
    </div>
  </section>
</template>

<script>
import { ArrowRight, Document, Folder, FolderOpened } from '@element-plus/icons-vue'

export default {
  name: 'MobileSidebarListNavigation',
  components: { ArrowRight, Document, Folder, FolderOpened },
  props: {
    label: { type: String, required: true },
    sectionIconName: { type: String, required: true },
    controlId: { type: String, required: true },
    overviewLabel: { type: String, required: true },
    overviewPath: { type: String, required: true },
    active: Boolean,
    overviewActive: Boolean,
    authenticated: Boolean,
    items: { type: Array, default: () => [] },
    loading: Boolean,
    error: Boolean,
    hasMore: Boolean,
    activeKey: { type: [String, Number], default: '' },
    itemKeyField: { type: String, default: 'id' },
    itemLabelField: { type: String, default: 'name' },
    itemActiveField: { type: String, default: 'navigationKey' },
    loginLabel: { type: String, required: true },
    errorLabel: { type: String, required: true },
    retryLabel: { type: String, required: true },
    emptyLabel: { type: String, required: true },
    moreLabel: { type: String, required: true }
  },
  emits: ['expand', 'load-more', 'retry', 'select', 'select-overview', 'request-login'],
  data() {
    return { expanded: false }
  },
  computed: {
    sectionIcon() {
      return this.sectionIconName === 'FolderOpened' ? FolderOpened : Document
    }
  },
  methods: {
    toggle() {
      if (!this.authenticated) {
        this.$emit('request-login')
        return
      }
      this.expanded = !this.expanded
      if (this.expanded) this.$emit('expand')
    },
    isCurrent(item) {
      return Boolean(this.activeKey) && String(item?.[this.itemActiveField]) === String(this.activeKey)
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-sidebar-list-navigation {
  min-width: 0;
  padding: 4px 0;
  color: var(--sidebar-text, #303442);
}
.mobile-sidebar-list-navigation-toggle,
.mobile-sidebar-list-navigation-item,
.mobile-sidebar-list-navigation-action {
  min-height: 44px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
}
.mobile-sidebar-list-navigation-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 10px 8px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  &[aria-expanded='true'] .mobile-sidebar-list-navigation-chevron { transform: rotate(90deg); }
}
.mobile-sidebar-list-navigation-label,
.mobile-sidebar-list-navigation-copy {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mobile-sidebar-list-navigation-chevron {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  color: var(--sidebar-muted, #5f687a);
  font-size: 14px;
}
.mobile-sidebar-list-navigation-options {
  min-width: 0;
  padding: 4px 0 0 12px;
}
.mobile-sidebar-list-navigation-list { display: grid; }
.mobile-sidebar-list-navigation-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 9px 8px;
  box-sizing: border-box;
}
.mobile-sidebar-list-navigation-icon {
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--sidebar-icon, #4b5263);

  .el-icon { width: 16px; height: 16px; font-size: 16px; }
}
.mobile-sidebar-list-navigation-toggle:hover,
.mobile-sidebar-list-navigation-item:hover,
.mobile-sidebar-list-navigation-action:hover {
  background: var(--sidebar-hover, #f1f2f5);
}
.mobile-sidebar-list-navigation-toggle:active,
.mobile-sidebar-list-navigation-item:active,
.mobile-sidebar-list-navigation-action:active {
  background: var(--sidebar-pressed, #e8eaef);
}
.mobile-sidebar-list-navigation-toggle.is-current,
.mobile-sidebar-list-navigation-item.is-current {
  background: var(--sidebar-selected-bg, #ede9fe);
  color: var(--sidebar-selected, #6d28d9);

  .mobile-sidebar-list-navigation-icon { color: inherit; }
}
.mobile-sidebar-list-navigation-status {
  margin: 4px 8px;
  color: var(--sidebar-muted, #5f687a);
  font-size: 13px;
  line-height: 1.5;
}
.mobile-sidebar-list-navigation-action {
  padding: 8px;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.mobile-sidebar-list-navigation-more {
  display: block;
  width: 100%;
  margin: 4px 0;
}
.mobile-sidebar-list-navigation-toggle:focus-visible,
.mobile-sidebar-list-navigation-item:focus-visible,
.mobile-sidebar-list-navigation-action:focus-visible {
  outline: 2px solid var(--sidebar-accent, #7c3aed);
  outline-offset: -2px;
}
</style>
